import {
  Button,
  ButtonVariantEnum,
  LoaderIcon,
  RightArrowIcon,
  StaticTableList
} from '@Wonder-Cave/ui';
import { IDropdownValue } from '@Wonder-Cave/ui/dist/Autocomplete';
import { axiosPost } from '@gr/portal/authAxios';
import { NotificationType, useNotifications } from '@gr/portal/contexts/NotificationContext';
import useRoles from '@gr/portal/hooks/useRoles';
import { MessageStatusEnum } from '@gr/shared/enums';
import { Auth0Role, capByTestNumber, IGetTestMessageResponse, IHttpResponse, ITestMessageRequest, MAX_TEST_MESSAGE_NUMBERS } from '@gr/shared/models';
import { groupBy } from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import TestNumbersDropdown from '../../shared/Form/Dropdowns/TestNumbersDropdown';
import { Radio } from '../../shared/Icons/Radio';
import CampaignMessagePreview from '../CampaignMessagePreview';
import { ISaveDraftResult } from '../types';
import { ICampaignPreview } from './types';

interface IPreviewProps {
  campaign: ICampaignPreview;
  submissionError?: string;
  status?: boolean;
  saveLoading: boolean;
  // A defined result is saveDraft's confirmed-success contract (see ISaveDraftResult) — the
  // send path below refuses to create a test batch without one when a save was required.
  // The parameter stays untyped for the same reason as saveDraft's own declaration
  saveDraft: (formikProps: any, exit: boolean) => Promise<ISaveDraftResult | undefined>;
  sending: boolean;
  setSending: (value: boolean) => void;
  setStatus?: (value: boolean) => void;
  // Reports the client a successful test send created its batch under, so the wizard can
  // advance the flat clientId the pre-seed guard compares against (MES-2599)
  onTestBatchSent?: (clientId: string) => void;
  values?: any;
}

/**
 * De-duplicates and caps a set of test numbers at the per-request send limit. TestNumbersDropdown
 * already clamps its own selection, but `numbers` is also seeded from a previous test batch and
 * read straight back out on send, so every write goes through here — the send would otherwise be
 * rejected wholesale by the server's own max check rather than being held to the cap up front.
 *
 * Delegates to the shared `capByTestNumber` (co-located with the cap constant it enforces) so
 * both layers agree on *which* entries survive; a second hand-written dedupe-and-slice here
 * could silently drift from it.
 *
 * @param testNumbers - Phone numbers destined for `numbers` or the send payload
 */
const capTestNumbers = (testNumbers: number[]): number[] =>
  capByTestNumber(testNumbers, (testNumber) => testNumber);

const Preview = ({ campaign, submissionError, status, saveLoading, saveDraft, sending, setSending, setStatus, onTestBatchSent, ...rest }: IPreviewProps) => {
  const [messages, setMessages] = useState<Array<any>>([]);
  const [numbers, setNumbers] = useState<number[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState<Date>();
  const [messagesScheduled, setMessagesScheduled] = useState(0);
  const isSuperAdmin = useRoles([Auth0Role.GR_ADMIN]);
  const { addNotification } = useNotifications();
  // Bumped when a new test send starts; fetches capture it before awaiting and
  // discard their response if it moved, so in-flight polls can't restore the old batch
  const fetchGeneration = useRef(0);
  // Only the most recent init call may clear the loading flag, so a stale call
  // resolving late can't enable the dropdown while a newer fetch is still in flight
  const initCallId = useRef(0);
  // The wizard carries the client in two places: the flat clientId (the client the latest
  // server-side test batch belongs to — set at draft load, advanced via onTestBatchSent only
  // after a send succeeds) and the live selector client.value — when they diverge, the latest
  // batch belongs to a different client than the current selection. Saving the draft alone must
  // NOT reconcile them: after a save under a new client followed by a failed send, the server
  // still holds the old client's batch
  const isTestBatchFromOtherClient = !!(campaign.client?.value && campaign.clientId && campaign.client?.value !== campaign.clientId);

  const initTestMessages = async () => {
    // Would rather not hit an if statement for each call, so this is a new function for the first call
    setLoading(true);
    const generation = fetchGeneration.current;
    const callId = ++initCallId.current;
    try {
      const messageResponse = await axiosPost<IHttpResponse<IGetTestMessageResponse>>(`/messages-get-test-messages`, {
        campaignId: campaign?.id,
      } as ITestMessageRequest);
      if (generation !== fetchGeneration.current) {
        return;
      }
      const messages = messageResponse?.data?.data?.testMessages ?? [];
      const scheduled = messageResponse?.data?.data?.scheduled;
      // Grid always mirrors the server; numbers only preselect when a batch exists so an
      // empty result doesn't wipe the user's dropdown selection — and only into an EMPTY
      // selection, because this init also re-runs whenever a failed save (the bail path) or a
      // failed send flips `sending` back off, and must not clobber numbers the user edited
      // before that attempt with the previous batch's numbers. Functional update: the init
      // effect doesn't depend on `numbers`, so the closure's copy may be stale. The pre-seed
      // is also skipped when the batch belongs to another client: sending its numbers would
      // auto-create them under the currently selected client (MES-2350)
      setMessages(messages);
      if (messages.length > 0 && !isTestBatchFromOtherClient) {
        setNumbers((prev) => (prev.length > 0 ? prev : capTestNumbers(messages.map((m) => Number(m.toNumber)))));
      }
      setMessagesScheduled(scheduled);
    } catch (e) {
      console.error(e);
    } finally {
      if (callId === initCallId.current) {
        setLoading(false);
      }
    }
  };

  const getTestMessages = async () => {
    const generation = fetchGeneration.current;
    try {
      if (!sending) {
        const messageResponse = await axiosPost<IHttpResponse<IGetTestMessageResponse>>(`/messages-get-test-messages`, {
          campaignId: campaign?.id,
        } as ITestMessageRequest);
        // has a new send started while waiting
        if (generation === fetchGeneration.current) {
          setMessages(messageResponse.data?.data?.testMessages ?? []);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (campaign?.id && !sending) {
      initTestMessages();

      const interval = setInterval(() => {
        getTestMessages();
      }, 5000);

      return () => clearInterval(interval);
    }
    // isTestBatchFromOtherClient keeps the pre-seed decision out of a stale closure — Preview
    // currently remounts per step, but the guard must not silently break if that ever changes
  }, [sending, campaign?.id, isTestBatchFromOtherClient]);

  const sendTestMessages = async () => {
    if (numbers.length > 0) {
      let savedCampaignId: string | undefined;
      const shouldSaveDraft = !campaign?.id || status;
      setSending(true);
      fetchGeneration.current += 1;
      setMessages([]);
      setMessagesScheduled(0);
      if (shouldSaveDraft) {
        // No confirmed save (undefined result), no send: the campaign is still persisted under
        // its previous client, so the batch — and any numbers MES-2350 would auto-create — would
        // land under that client while the user is looking at the new one. status stays true,
        // so the retry re-attempts the save. The grid wiped above is restored by the init effect
        // when sending flips back off; the user's number selection survives that re-init (the
        // pre-seed only fills an empty selection), so the retry targets what they chose
        const result = await saveDraft(rest, false);
        if (!result) {
          addNotification({
            header: 'Test message not sent',
            content: 'The campaign draft could not be saved. Fix the save error and try again.',
            type: NotificationType.FAILURE,
          });
          setSending(false);
          return;
        }
        savedCampaignId = result.response.data?.data?.campaignId;
      }
      setDate(new Date()); // Record the date last clicked
      try {
        await axiosPost(`/v1/campaigns/${savedCampaignId ?? campaign?.id ?? rest?.values?.id}/test-messages`, {
          testNumbers: capTestNumbers(numbers.map((n) => Number(n))),
        });
        setMessagesScheduled(numbers.length);
        // Only a successful send may advance the pre-seed guard's clientId. Any save this send
        // required was already confirmed above (the failed-save path bails before posting), so
        // the persisted campaign, the new batch, and the live selection all agree on the client
        onTestBatchSent?.(rest?.values?.client?.value ?? campaign.client?.value);
      } catch (e: any) {
        console.error(e);
        addNotification({
          header: 'Error',
          content: e.response?.data?.message ?? 'Unexpected error creating test messages.',
          type: NotificationType.FAILURE,
        });
      } finally {
        // The unsaved-changes flag tracks the SAVE, not the send: every path reaching here
        // either needed no save or had it confirmed, so the form now matches the persisted
        // campaign even when the send itself failed
        setStatus!(false);
        setSending(false);
      }
    } else {
      setError(true);
    }
  };

  const formatPhoneNumber = (phoneNumberString: string) => {
    var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      var intlCode = match[1] ? '+1 ' : '';
      return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
    }
    return phoneNumberString;
  };

  const groupedMessages = groupBy(messages, 'status');

  // The dropdown displays more numbers than a single test send accepts, so the cap has to be
  // legible — a clamped Select All is otherwise silently confusing
  const isAtTestNumberCap = numbers.length >= MAX_TEST_MESSAGE_NUMBERS;
  const testNumberHint = isAtTestNumberCap
    ? `Maximum of ${MAX_TEST_MESSAGE_NUMBERS} contacts selected. Remove one to select a different contact.`
    : `Select up to ${MAX_TEST_MESSAGE_NUMBERS} contacts to send a test message. ${numbers.length} of ${MAX_TEST_MESSAGE_NUMBERS} selected.`;

  // const sentCount =
  //   (groupedMessages['SENT']?.length ?? 0) +
  //   (groupedMessages['DELIVERED']?.length ?? 0) +
  //   (groupedMessages['FAILED']?.length ?? 0);

  return (
    <div className="flex">
      <div className="flex basis-1/2">
        <CampaignMessagePreview campaign={campaign} domain={campaign.domain} />
      </div>
      <div className="flex flex-col justify-between basis-1/2">
        <div>
          <h2 className="mt-12">Preview your message</h2>
          <div className="mt-8">
            <TestNumbersDropdown
              value={numbers}
              loading={loading}
              onChange={(value) => {
                setNumbers(capTestNumbers((value as IDropdownValue[]).map((v) => v?.additionalData?.number)));
                setError(false);
              }}
              showError={false}
              client={campaign.client?.value ?? campaign.clientId ?? ''}
            />
            {/* TODO: Add a description to the component */}
            <div className={`${error ? 'text-flamingo' : 'text-medium-gray'} -mt-2 body-text-small`}>{testNumberHint}</div>
            <div className="flex items-center mt-8">
              <Button
                trailingIcon={<RightArrowIcon />}
                variant={ButtonVariantEnum.GREEN}
                onClick={sendTestMessages}
                type="button"
                className="[&]:place-items-end" // The arrow icon makes the text sit slightly above center and this is the fix
                isLoading={sending}
              >
                Send Test Message
              </Button>
              {saveLoading && <div className="flex items-center ml-6">
                <LoaderIcon />
                <p className="mb-px ml-3 body-text-bold text-medium-gray">
                  Saving draft...
                </p>
              </div>}
            </div>
            {!sending && messages?.length > 0 && (
              <StaticTableList
                className="mt-8 basis-full"
                columns={[
                  {
                    headerName: 'NUMBER',
                    fieldName: 'toNumber',
                    renderColumn: (row) => (
                      <div className="flex items-center whitespace-nowrap">
                        {formatPhoneNumber(row.toNumber)}
                        {row.status === MessageStatusEnum.DELIVERED && (
                          <div className="mx-2">
                            <Radio />
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    headerName: 'CARRIER',
                    fieldName: 'carrierName',
                  },
                  {
                    headerName: 'ERROR CODE',
                    fieldName: 'errorCode',
                  },
                  {
                    headerName: 'ERROR TYPE',
                    fieldName: 'errorType',
                  },
                  ...(isSuperAdmin
                    ? [
                      {
                        headerName: 'PROVIDER ERROR CODE',
                        fieldName: 'providerErrorCode',
                      },
                      {
                        headerName: 'PROVIDER ERROR TYPE',
                        fieldName: 'providerErrorType',
                      },
                    ]
                    : []),
                ]}
                data={messages}
              />
            )}
            <div className="status-box rounded-large border-white border-solid border-[5px] flex flex-col mt-8 mb-4 max-w-lg">
              <h3 className="mx-auto mt-6 text-wc-blue">Test Message Progress</h3>
              <h5 className="mx-auto mt-2 text-dark-gray">STATUS: {messages[0]?.status ?? 'SENT'}</h5>
              <div className="mx-8 mt-6">
                <div className="flex items-center mt-4">
                  <h5 className="min-w-[65px] text-right mt-px mr-2 basis-1/4 text-dark-gray">DELIVERY</h5>
                  <div
                    className={`flex grow h-2 overflow-hidden text-xs rounded justify-end ${messagesScheduled === 0 ? 'bg-light-gray dark:bg-slate-400' : 'bg-mint'
                      }`}
                  >
                    <>
                      <div
                        style={{
                          width: `${Math.min(
                            Math.ceil(((groupedMessages['DELIVERED']?.length ?? 0) / (messagesScheduled ?? 100)) * 100),
                            100
                          )}%`,
                        }}
                        className={`flex flex-col justify-center text-center text-white bg-grass shadow-none bg-grass-400 whitespace-nowrap`}
                      ></div>
                      <div
                        style={{
                          width: `${Math.min(
                            Math.ceil(((groupedMessages['FAILED']?.length ?? 0) / (messagesScheduled ?? 100)) * 100),
                            100
                          )}%`,
                        }}
                        className={`flex flex-col justify-center text-center text-white bg-flamingo shadow-none whitespace-nowrap`}
                      ></div>
                    </>
                  </div>
                </div>
                <h5
                  className="mt-1 text-right truncate text-medium-gray dark:text-slate-400 basis-full"
                  title="Sent / Delivered / Failed"
                >
                  {messagesScheduled.toLocaleString()} / {(groupedMessages['DELIVERED']?.length ?? 0).toLocaleString()} /{' '}
                  {(groupedMessages['FAILED']?.length ?? 0).toLocaleString()}
                </h5>
              </div>
              <div className="mx-auto my-8 body-text-small text-medium-gray">Sent: {moment(date).format('L, LT')}</div>
            </div>
          </div>
        </div>
        {submissionError && <h3 className='mb-4 text-right text-red-600'>{submissionError}</h3>}
      </div>
    </div>
  );
};

export default Preview;
